import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MapPin, MagnifyingGlass } from '@phosphor-icons/react';
import { Autocomplete, type AutocompleteHandle } from '../../Autocomplete';
import { useSearchContextOptional } from '../../../contexts/SearchContext';
import { primaryNavHrefByKey, type PrimaryNavLocale } from '../primary-nav-links';
import { navIndexMessages } from './nav-index-messages';
import {
  locationSegmentClass,
  NAV_PART,
  searchWrapperClass,
  SEARCH_ICON_WRAP,
  SEARCH_INPUT,
  SUGGESTIONS_EDGE_FADE_LEFT,
  SUGGESTIONS_EDGE_FADE_RIGHT,
  SUGGESTIONS_EMPTY,
  SUGGESTIONS_HEADER,
  SUGGESTIONS_HEADER_HINT,
  SUGGESTIONS_LIST_WRAP,
  SUGGESTIONS_PANEL,
  SUGGESTIONS_ROW_ATTR_LABEL,
  SUGGESTIONS_ROW_COUNT,
  SUGGESTIONS_SCROLL_HINT_THRESHOLD,
  suggestionsRowClass,
} from './nav-styles';
import { fetchNavSearchSuggestions, type NavSearchSuggestion } from './search-suggestions-client';
import type { IntlShape } from 'react-intl';

/** Same key AllFiltersModal already reads its initial tab from (see DesktopFilterCategories'
 * handleLocationClick); writing it before navigating to the jobs route makes the modal open
 * straight to Location once it mounts there, same as clicking the SRP's own location button. */
const ALL_FILTERS_OPEN_SECTION_KEY = 'allFiltersModal.openSection';
/** One-shot flag: the SRP opens the filters modal once on arrival, then clears it (SearchWrapper). */
export const ALL_FILTERS_AUTO_OPEN_KEY = 'allFiltersModal.autoOpen';

/** Debounce delay for the fallback launcher's suggestions fetch: matches the static launcher's
 * own script (web-static/pages/landing/landing-nav-live-index-script.ts), spec item 2. */
const SUGGESTIONS_DEBOUNCE_MS = 150;

function navigateToLocationSection(locale: PrimaryNavLocale): void {
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(ALL_FILTERS_OPEN_SECTION_KEY, 'location');
      window.localStorage.setItem(ALL_FILTERS_AUTO_OPEN_KEY, '1');
    } catch {
      // Private browsing / storage disabled: the modal just won't default to Location, no crash.
    }
  }
  window.location.assign(primaryNavHrefByKey('jobs', locale));
}

type FormatMessage = IntlShape['formatMessage'];
type LauncherSizeVariant = 'compact40' | 'compact44';

interface SearchLauncherProps {
  locale: PrimaryNavLocale;
  formatMessage: FormatMessage;
  /** 'compact40' (default): the desktop row-one 40px control. 'compact44': the mobile bottom
   * nav's 44px minimum tap target (TEC navbar redesign Phase 2 item 2.7). */
  sizeVariant?: LauncherSizeVariant;
  /** Facet-suggestions dropdown opens upward instead of down, same as MobileBottomNav's own
   * Autocomplete usage, since that bar is fixed to the bottom of the viewport. */
  popUp?: boolean;
  onOpen?: () => void;
  /** MobileBottomNav's own on-SRP search field has never had a fused location segment (location
   * lives behind the separate filters button there); off-SRP it reuses this component but keeps
   * that same shape rather than introducing a new location button the bottom bar never had. */
  showLocationSegment?: boolean;
}

/**
 * Exported (TEC navbar redesign Phase 4 item 1) so ResourceNavbar.tsx, which has no
 * SearchProvider/FacetsContext either, can render the exact same location button server-side:
 * this component takes no hooks and no context, only props, so it is safe in a prerendered page.
 */
export const LocationSegment: React.FC<{ locale: PrimaryNavLocale; formatMessage: FormatMessage; sizeVariant: LauncherSizeVariant }> = ({
  locale,
  formatMessage,
  sizeVariant,
}) => {
  const label = formatMessage({
    id: 'search.refinements.location',
    defaultMessage: 'Location',
    description: 'Filter label for location selection in search refinements',
  });
  return (
    <button
      type="button"
      onClick={() => navigateToLocationSection(locale)}
      data-jc-nav-part={NAV_PART.locationSegment}
      className={locationSegmentClass(sizeVariant)}
      aria-label={label}
    >
      <MapPin weight="fill" className="w-4 h-4 shrink-0 text-jc-accent" />
      <span className="hidden xl:inline whitespace-nowrap">{label}</span>
    </button>
  );
};

/** Non-SRP variant: a real SearchProvider is mounted (most pages), so the exact same Autocomplete
 * used on the SRP renders here too, with the new onSubmitNavigate hook wired to a real page
 * navigation instead of the SRP's in-place pushState. */
const LiveLauncher: React.FC<Required<Pick<SearchLauncherProps, 'locale' | 'formatMessage' | 'sizeVariant' | 'popUp' | 'showLocationSegment'>> & { onOpen?: () => void }> = ({
  locale,
  formatMessage,
  sizeVariant,
  popUp,
  onOpen,
  showLocationSegment,
}) => {
  const autocompleteRef = useRef<AutocompleteHandle>(null);
  // Full document loads, not vike's client-side navigate(): the SRP is rendered by its own
  // client island (pages/index/srp.client.ts) and does not mount through a client-side route
  // transition from another page; navigate() only swapped the URL and left the page in place.
  const handleSubmitNavigate = useCallback((url: string) => {
    window.location.assign(url);
  }, []);
  return (
    <div className="min-w-0 flex-1 flex items-center max-w-[600px] rounded-full transition-shadow duration-200 ease-out motion-reduce:transition-none focus-within:shadow-[0_0_0_4px_rgba(59,94,69,0.18),0_6px_28px_-6px_rgba(59,94,69,0.28)] dark:focus-within:shadow-[0_0_0_4px_rgba(152,207,168,0.16),0_6px_28px_-6px_rgba(152,207,168,0.30)]">
      <div className="min-w-0 flex-1">
        <Autocomplete
          ref={autocompleteRef}
          attached={showLocationSegment}
          popUp={popUp}
          onOpen={onOpen}
          sizeVariant={sizeVariant}
          placeholder={formatMessage(navIndexMessages.searchPlaceholder)}
          onSubmitNavigate={handleSubmitNavigate}
        />
      </div>
      {showLocationSegment ? <LocationSegment locale={locale} formatMessage={formatMessage} sizeVariant={sizeVariant} /> : null}
    </div>
  );
};

/**
 * Facet-suggestions dropdown for the fallback launcher (TEC navbar redesign Phase 4 item 2):
 * shaped after the SRP's own Autocomplete popover (components/Autocomplete.tsx), simplified
 * (no scroll-gradient overlays, no portal) since this only ever renders under a row-one control,
 * never the bottom-nav pop-up.
 */
const FallbackSuggestionsDropdown: React.FC<{
  suggestions: NavSearchSuggestion[];
  highlightedIndex: number;
  noMatchesLabel: string;
  filterMatchesLabel: string;
  scrollHintLabel: string;
  onPick: (suggestion: NavSearchSuggestion) => void;
  onHover: (index: number) => void;
  query: string;
}> = ({ suggestions, highlightedIndex, noMatchesLabel, filterMatchesLabel, scrollHintLabel, onPick, onHover, query }) => (
  <div role="listbox" className={SUGGESTIONS_PANEL}>
    {suggestions.length > 0 ? (
      <div className={SUGGESTIONS_LIST_WRAP}>
        <div className={SUGGESTIONS_EDGE_FADE_LEFT} />
        <div className={SUGGESTIONS_EDGE_FADE_RIGHT} />
        <div className={SUGGESTIONS_HEADER}>
          <span>{filterMatchesLabel}</span>
          {suggestions.length > SUGGESTIONS_SCROLL_HINT_THRESHOLD && (
            <span className={SUGGESTIONS_HEADER_HINT}>{scrollHintLabel}</span>
          )}
        </div>
        {suggestions.map((item, index) => (
          <button
            key={`${item.attribute}:${item.value}`}
            type="button"
            role="option"
            aria-selected={index === highlightedIndex}
            onMouseEnter={() => onHover(index)}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => onPick(item)}
            className={suggestionsRowClass(index === highlightedIndex)}
          >
            <span className="flex flex-col">
              <span className={SUGGESTIONS_ROW_ATTR_LABEL}>{item.attributeLabel}</span>
              <span>{item.label}</span>
            </span>
            <span className={SUGGESTIONS_ROW_COUNT}>{item.count.toLocaleString()}</span>
          </button>
        ))}
      </div>
    ) : query.trim() ? (
      <div className={SUGGESTIONS_EMPTY}>{noMatchesLabel}</div>
    ) : null}
  </div>
);

/** MinimalChrome pages (resources, about, for-employers, locations, authors, tax-iceberg) mount
 * no SearchProvider, so the live Autocomplete would throw. This is a plain, dependency-free
 * input that submits a real navigation to the jobs route with `search=` (TEC navbar redesign
 * Phase 4 item 3: the results page reads the query from `search=`/`query=`, never `q=`, see
 * parseUrlToIndexUiState in utils/search-router/url-handling.ts: a bare `?q=` silently dropped
 * the typed query). It also now fetches the same GET /api/search-suggestions route the static
 * launcher's script fetches (Phase 4 item 2), so MinimalChrome pages get facet suggestions too. */
const FallbackLauncher: React.FC<
  Required<Pick<SearchLauncherProps, 'locale' | 'formatMessage' | 'sizeVariant' | 'showLocationSegment'>>
> = ({ locale, formatMessage, sizeVariant, showLocationSegment }) => {
  const [value, setValue] = useState('');
  const [suggestions, setSuggestions] = useState<NavSearchSuggestion[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestSeqRef = useRef(0);

  const runFetch = useCallback(
    (query: string) => {
      const seq = ++requestSeqRef.current;
      fetchNavSearchSuggestions(locale, query).then((results) => {
        if (seq !== requestSeqRef.current) return; // a newer keystroke already superseded this request
        setSuggestions(results);
        setHighlightedIndex(-1);
      });
    },
    [locale],
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isOpen]);

  const navigateToQuery = useCallback(
    (query: string) => {
      const base = primaryNavHrefByKey('jobs', locale);
      const trimmed = query.trim();
      window.location.assign(trimmed ? `${base}?search=${encodeURIComponent(trimmed)}` : base);
    },
    [locale],
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const next = event.target.value;
      setValue(next);
      setIsOpen(true);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => runFetch(next), SUGGESTIONS_DEBOUNCE_MS);
    },
    [runFetch],
  );

  const handleFocus = useCallback(() => {
    setIsOpen(true);
    if (suggestions.length === 0) runFetch(value);
  }, [runFetch, suggestions.length, value]);

  /** A click on an input that never lost DOM focus (e.g. an earlier outside-click closed the
   * panel via `isOpen`/state alone, without blurring the input) does not re-fire `focus` — the
   * browser only fires it on an actual focus transition. Without this, clicking the bar a second
   * time silently did nothing: `isOpen` stayed false and the dropdown never came back. Mirrors
   * the same "focus does not re-fire" click handler the old landing-search-script.ts already had
   * to add for this exact reason. */
  const handleInputClick = useCallback(() => {
    if (!isOpen) {
      setIsOpen(true);
      if (suggestions.length === 0) runFetch(value);
    }
  }, [isOpen, runFetch, suggestions.length, value]);

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
        window.location.assign(suggestions[highlightedIndex].href);
        return;
      }
      navigateToQuery(value);
    },
    [highlightedIndex, navigateToQuery, suggestions, value],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          if (suggestions.length === 0) runFetch(value);
          return;
        }
        setHighlightedIndex((index) => (suggestions.length === 0 ? -1 : Math.min(index + 1, suggestions.length - 1)));
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setHighlightedIndex((index) => Math.max(index - 1, -1));
      } else if (event.key === 'Escape') {
        setIsOpen(false);
      }
    },
    [isOpen, runFetch, suggestions.length, value],
  );

  const noMatchesLabel = formatMessage({
    id: 'autocomplete.noMatches',
    defaultMessage: 'No filter matches found',
    description: 'Message shown in autocomplete dropdown when no filter suggestions match the search query',
  });
  const filterMatchesLabel = formatMessage({
    id: 'autocomplete.filters',
    defaultMessage: 'Filter matches',
    description: 'Section header in autocomplete dropdown showing filter suggestions that match the search query',
  });
  const scrollHintLabel = formatMessage({
    id: 'autocomplete.scrollHint',
    defaultMessage: 'Scroll for more',
    description: 'Hint text shown when there are more filter suggestions available that can be viewed by scrolling',
  });

  return (
    <div ref={containerRef} className="relative min-w-0 flex-1 max-w-[600px]">
      <form
        onSubmit={handleSubmit}
        data-jc-nav-part={NAV_PART.searchWrapper}
        className={searchWrapperClass(sizeVariant, isOpen)}
      >
        <span className={SEARCH_ICON_WRAP} aria-hidden>
          <MagnifyingGlass weight="fill" className="w-4 h-4" />
        </span>
        <input
          type="text"
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onClick={handleInputClick}
          onKeyDown={handleKeyDown}
          placeholder={formatMessage(navIndexMessages.searchPlaceholder)}
          role="combobox"
          aria-expanded={isOpen}
          aria-autocomplete="list"
          data-jc-nav-part={NAV_PART.searchInput}
          className={SEARCH_INPUT}
        />
        {showLocationSegment ? <LocationSegment locale={locale} formatMessage={formatMessage} sizeVariant={sizeVariant} /> : null}
      </form>
      {isOpen ? (
        <FallbackSuggestionsDropdown
          suggestions={suggestions}
          highlightedIndex={highlightedIndex}
          noMatchesLabel={noMatchesLabel}
          filterMatchesLabel={filterMatchesLabel}
          scrollHintLabel={scrollHintLabel}
          query={value}
          onHover={setHighlightedIndex}
          onPick={(suggestion) => window.location.assign(suggestion.href)}
        />
      ) : null}
    </div>
  );
};

/**
 * Row-one search bar for the navbar live-index redesign (TEC navbar redesign Phase 1). On the
 * SRP, DesktopFilterCategories keeps using SearchNavbar's own live Autocomplete directly (that
 * behaviour is unchanged by this component). Everywhere else, this picks between the live
 * Autocomplete (when a SearchProvider is mounted, i.e. every page except MinimalChrome's
 * resources/about/for-employers/locations/authors/tax-iceberg) and a plain fallback input.
 *
 * Phase 2 item 2.7 reuses this same component (sizeVariant="compact44", popUp) for
 * MobileBottomNav's search field on non-SRP pages, so there is exactly one
 * "has a SearchProvider or not" branch in the whole redesign, not two.
 */
export const SearchLauncher: React.FC<SearchLauncherProps> = ({
  locale,
  formatMessage,
  sizeVariant = 'compact40',
  popUp = false,
  onOpen,
  showLocationSegment = true,
}) => {
  const hasSearchContext = useSearchContextOptional() !== undefined;
  return hasSearchContext ? (
    <LiveLauncher
      locale={locale}
      formatMessage={formatMessage}
      sizeVariant={sizeVariant}
      popUp={popUp}
      onOpen={onOpen}
      showLocationSegment={showLocationSegment}
    />
  ) : (
    <FallbackLauncher locale={locale} formatMessage={formatMessage} sizeVariant={sizeVariant} showLocationSegment={showLocationSegment} />
  );
};
